import React, { useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BarChartIcon, LineChartIcon, AreaChartIcon } from "../SvgIcon";

const staticData = [
  { area: "London", totalJobs: 520, bookedJobs: 380, unquotedJobs: 75 },
  { area: "Manchester", totalJobs: 310, bookedJobs: 220, unquotedJobs: 48 },
  { area: "Birmingham", totalJobs: 275, bookedJobs: 190, unquotedJobs: 40 },
  { area: "Leeds", totalJobs: 198, bookedJobs: 140, unquotedJobs: 32 },
  { area: "Bristol", totalJobs: 165, bookedJobs: 118, unquotedJobs: 27 },
  { area: "Sheffield", totalJobs: 142, bookedJobs: 98, unquotedJobs: 22 },
  { area: "Liverpool", totalJobs: 130, bookedJobs: 89, unquotedJobs: 19 },
  { area: "Edinburgh", totalJobs: 112, bookedJobs: 76, unquotedJobs: 15 },
  { area: "Glasgow", totalJobs: 104, bookedJobs: 72, unquotedJobs: 14 },
  { area: "Cardiff", totalJobs: 88, bookedJobs: 60, unquotedJobs: 11 },
];

const COLORS = {
  totalJobs: "#3b82f6",
  bookedJobs: "#10b981",
  unquotedJobs: "#f59e0b",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{label}</p>
      <div className="chart-tooltip__content">
        {payload.map((entry, i) => (
          <div key={i} className="chart-tooltip__entry">
            <span className="chart-tooltip__label">{entry.name}:</span>
            <span className="chart-tooltip__value chart-tooltip__value--current">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CHART_MARGIN = { top: 15, right: 30, left: 40, bottom: 80 };

const RptJobs = () => {
  const [activeTab, setActiveTab] = useState("table");
  const [chartType, setChartType] = useState("bar");

  const totals = staticData.reduce(
    (acc, row) => ({
      totalJobs: acc.totalJobs + row.totalJobs,
      bookedJobs: acc.bookedJobs + row.bookedJobs,
      unquotedJobs: acc.unquotedJobs + row.unquotedJobs,
    }),
    { totalJobs: 0, bookedJobs: 0, unquotedJobs: 0 },
  );

  const commonAxisProps = {
    xAxis: {
      dataKey: "area",
      stroke: "#9ca3af",
      className: "chart-axis-x",
      angle: -45,
      textAnchor: "end",
      height: 80,
      interval: 0,
    },
    yAxis: { stroke: "#9ca3af", className: "chart-axis-y", width: 50 },
    grid: { strokeDasharray: "3 3", stroke: "#e5e7eb", vertical: false },
  };

  const renderChart = () => {
    const defs = (
      <defs>
        <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity={1} />
        </linearGradient>
        <linearGradient id="gradBooked" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" stopOpacity={1} />
          <stop offset="100%" stopColor="#10b981" stopOpacity={1} />
        </linearGradient>
        <linearGradient id="gradUnquoted" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fcd34d" stopOpacity={1} />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity={1} />
        </linearGradient>
        <linearGradient id="areaTotal" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.7} />
          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
        </linearGradient>
        <linearGradient id="areaBooked" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#10b981" stopOpacity={0.7} />
          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
        </linearGradient>
        <linearGradient id="areaUnquoted" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.7} />
          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
        </linearGradient>
      </defs>
    );

    if (chartType === "bar") {
      return (
        <BarChart data={staticData} margin={CHART_MARGIN}>
          {defs}
          <CartesianGrid {...commonAxisProps.grid} />
          <XAxis {...commonAxisProps.xAxis} />
          <YAxis {...commonAxisProps.yAxis} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperClassName="chart-legend" />
          <Bar
            dataKey="totalJobs"
            name="Total Jobs"
            fill="url(#gradTotal)"
            radius={[6, 6, 0, 0]}
          />
          <Bar
            dataKey="bookedJobs"
            name="Booked Jobs"
            fill="url(#gradBooked)"
            radius={[6, 6, 0, 0]}
          />
          <Bar
            dataKey="unquotedJobs"
            name="Unquoted Jobs"
            fill="url(#gradUnquoted)"
            radius={[6, 6, 0, 0]}
          />
        </BarChart>
      );
    }

    if (chartType === "line") {
      return (
        <LineChart data={staticData} margin={CHART_MARGIN}>
          {defs}
          <CartesianGrid {...commonAxisProps.grid} />
          <XAxis {...commonAxisProps.xAxis} />
          <YAxis {...commonAxisProps.yAxis} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperClassName="chart-legend" iconType="line" />
          <Line
            type="monotone"
            dataKey="totalJobs"
            name="Total Jobs"
            stroke={COLORS.totalJobs}
            strokeWidth={3}
            dot={{ fill: COLORS.totalJobs, r: 5 }}
            activeDot={{ r: 7 }}
          />
          <Line
            type="monotone"
            dataKey="bookedJobs"
            name="Booked Jobs"
            stroke={COLORS.bookedJobs}
            strokeWidth={3}
            dot={{ fill: COLORS.bookedJobs, r: 5 }}
            activeDot={{ r: 7 }}
          />
          <Line
            type="monotone"
            dataKey="unquotedJobs"
            name="Unquoted Jobs"
            stroke={COLORS.unquotedJobs}
            strokeWidth={3}
            dot={{ fill: COLORS.unquotedJobs, r: 5 }}
            activeDot={{ r: 7 }}
          />
        </LineChart>
      );
    }

    return (
      <AreaChart data={staticData} margin={CHART_MARGIN}>
        {defs}
        <CartesianGrid {...commonAxisProps.grid} />
        <XAxis {...commonAxisProps.xAxis} />
        <YAxis {...commonAxisProps.yAxis} />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperClassName="chart-legend" />
        <Area
          type="monotone"
          dataKey="totalJobs"
          name="Total Jobs"
          stroke={COLORS.totalJobs}
          strokeWidth={2}
          fill="url(#areaTotal)"
        />
        <Area
          type="monotone"
          dataKey="bookedJobs"
          name="Booked Jobs"
          stroke={COLORS.bookedJobs}
          strokeWidth={2}
          fill="url(#areaBooked)"
        />
        <Area
          type="monotone"
          dataKey="unquotedJobs"
          name="Unquoted Jobs"
          stroke={COLORS.unquotedJobs}
          strokeWidth={2}
          fill="url(#areaUnquoted)"
        />
      </AreaChart>
    );
  };

  return (
    <div className="l-base-layout__content">
      <h1>Admin / Reports / Jobs Report</h1>
      <div className="c-hr" />

      <div className="l-row">
        <div className="l-col-100 u-padding-top-0 u-padding-bottom-0">
          <div className="c-filter-bar-2">
            <div className="item-container">
              <div className="view-buttons-group">
                <button
                  type="button"
                  className={`view-button ${activeTab === "table" ? "active" : ""}`}
                  onClick={() => setActiveTab("table")}
                >
                  View Table
                </button>
                <button
                  type="button"
                  className={`view-button ${activeTab === "graph" ? "active" : ""}`}
                  onClick={() => setActiveTab("graph")}
                >
                  View Graph
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="l-col-100">
          {activeTab === "table" ? (
            <>
              <div className="rpt-stat-cards">
                <div className="rpt-stat-card total">
                  <div className="rpt-stat-icon total">📋</div>
                  <div className="rpt-stat-info">
                    <div className="rpt-stat-label">Total Jobs</div>
                    <div className="rpt-stat-value">
                      {totals.totalJobs.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="rpt-stat-card booked">
                  <div className="rpt-stat-icon booked">✅</div>
                  <div className="rpt-stat-info">
                    <div className="rpt-stat-label">Booked Jobs</div>
                    <div className="rpt-stat-value">
                      {totals.bookedJobs.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="rpt-stat-card unquoted">
                  <div className="rpt-stat-icon unquoted">⏳</div>
                  <div className="rpt-stat-info">
                    <div className="rpt-stat-label">Unquoted Jobs</div>
                    <div className="rpt-stat-value">
                      {totals.unquotedJobs.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rpt-data-table-wrapper">
                <table className="rpt-data-table">
                  <thead>
                    <tr>
                      <th>Area</th>
                      <th>
                        <span className="th-dot blue" />
                        Total Jobs
                      </th>
                      <th>
                        <span className="th-dot green" />
                        Booked Jobs
                      </th>
                      <th>
                        <span className="th-dot amber" />
                        Unquoted Jobs
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {staticData.map((row) => (
                      <tr key={row.area}>
                        <td>{row.area}</td>
                        <td
                          style={{ color: COLORS.totalJobs, fontWeight: 600 }}
                        >
                          {row.totalJobs.toLocaleString()}
                        </td>
                        <td
                          style={{ color: COLORS.bookedJobs, fontWeight: 600 }}
                        >
                          {row.bookedJobs.toLocaleString()}
                        </td>
                        <td
                          style={{
                            color: COLORS.unquotedJobs,
                            fontWeight: 600,
                          }}
                        >
                          {row.unquotedJobs.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="rpt-charts-container">
              <div className="charts-controls">
                <div className="controls-left" />
                <div className="chart-type-tabs">
                  <button
                    className={`chart-tab ${chartType === "bar" ? "active" : ""}`}
                    onClick={() => setChartType("bar")}
                    title="Bar Chart"
                  >
                    <BarChartIcon />
                  </button>
                  <button
                    className={`chart-tab ${chartType === "line" ? "active" : ""}`}
                    onClick={() => setChartType("line")}
                    title="Line Chart"
                  >
                    <LineChartIcon />
                  </button>
                  <button
                    className={`chart-tab ${chartType === "area" ? "active" : ""}`}
                    onClick={() => setChartType("area")}
                    title="Area Chart"
                  >
                    <AreaChartIcon />
                  </button>
                </div>
              </div>

              <div className="chart-wrapper">
                <div className="chart-header">
                  <p className="chart-description">
                    Area wise — Total Jobs, Booked Jobs &amp; Unquoted Jobs
                  </p>
                </div>
                <ResponsiveContainer width="100%" height={420}>
                  {renderChart()}
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RptJobs;
